// Cookie utilities for filter persistence
import {
  SalesOrderDraftStatus,
  SalesOrderSource,
} from 'src/types/application/SalesOrderDraftDTO';
import { FilterValues } from '../components/SalesOrderFilters';
import { WatchStatus } from '@/types/application/WatcherDTO';

// Filters persisted in cookie
export type ExtendedFilterValues = FilterValues;

const FILTER_COOKIE_NAME = 'sales_order_drafts_filters';
const COOKIE_EXPIRY_DAYS = 180;

const VALID_WATCH_STATUSES: WatchStatus[] = ['watching', 'assigned'];

export const saveFiltersToCookie = (filters: FilterValues) => {
  try {
    const extendedFilters: ExtendedFilterValues = { ...filters };
    const filterData = JSON.stringify(extendedFilters);
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + COOKIE_EXPIRY_DAYS);
    document.cookie = `${FILTER_COOKIE_NAME}=${encodeURIComponent(
      filterData,
    )}; expires=${expiryDate.toUTCString()}; path=/`;
  } catch (error) {
    console.warn('Failed to save filters to cookie:', error);
  }
};

export const loadFiltersFromCookie = (): ExtendedFilterValues => {
  try {
    const cookies = document.cookie.split(';');
    const filterCookie = cookies.find((cookie) =>
      cookie.trim().startsWith(`${FILTER_COOKIE_NAME}=`),
    );

    if (filterCookie) {
      const filterData = decodeURIComponent(filterCookie.split('=')[1]);
      const parsedFilters = JSON.parse(
        filterData,
      ) as Partial<ExtendedFilterValues>;

      // Validate filters and keep valid ones
      const validatedFilters: ExtendedFilterValues = {};

      // Validate and keep valid statuses
      if (parsedFilters.statuses && Array.isArray(parsedFilters.statuses)) {
        const validStatuses = parsedFilters.statuses.filter((s: string) =>
          Object.values(SalesOrderDraftStatus).includes(
            s as SalesOrderDraftStatus,
          ),
        );
        if (validStatuses.length) {
          validatedFilters.statuses = validStatuses as SalesOrderDraftStatus[];
        }
      }

      // Validate and keep valid sources
      if (parsedFilters.sources && Array.isArray(parsedFilters.sources)) {
        const validSources = parsedFilters.sources.filter((s: string) =>
          Object.values(SalesOrderSource).includes(s as SalesOrderSource),
        );
        if (validSources.length) {
          validatedFilters.sources = validSources as SalesOrderSource[];
        }
      }

      // Validate and keep valid watch statuses
      if (
        parsedFilters.watchStatus &&
        Array.isArray(parsedFilters.watchStatus)
      ) {
        const validWatchStatuses = parsedFilters.watchStatus.filter(
          (w: string) => VALID_WATCH_STATUSES.includes(w as WatchStatus),
        );
        if (validWatchStatuses.length) {
          validatedFilters.watchStatus = validWatchStatuses as WatchStatus[];
        }
      }

      // Remove legacy customerId fields if present

      // Validate customerIds array
      if (Array.isArray(parsedFilters.customerIds)) {
        const uniqueIds = [...new Set(parsedFilters.customerIds)].filter(
          (id): id is string => typeof id === 'string' && id.length > 0,
        );
        if (uniqueIds.length) {
          validatedFilters.customerIds = uniqueIds;
        }
      }

      // Validate excludedCustomerIds array
      if (Array.isArray(parsedFilters.excludedCustomerIds)) {
        const uniqueIds = [
          ...new Set(parsedFilters.excludedCustomerIds),
        ].filter((id): id is string => typeof id === 'string' && id.length > 0);
        if (uniqueIds.length) {
          validatedFilters.excludedCustomerIds = uniqueIds;
        }
      }

      // Validate purchaseOrderNumber string
      if (
        typeof parsedFilters.purchaseOrderNumber === 'string' &&
        parsedFilters.purchaseOrderNumber.length > 0
      ) {
        validatedFilters.purchaseOrderNumber =
          parsedFilters.purchaseOrderNumber;
      }

      return validatedFilters;
    }
  } catch (error) {
    console.warn('Failed to load filters from cookie:', error);
  }

  // Return empty filters if loading fails or no cookie exists
  return {};
};
